// Home section order and which sections are hidden. Pure, so the
// migration of saved layouts is testable.

// Section keys MUST match the hv-cell-* class suffixes in theme.css.
// CSS still ships baseline order: rules so an unstyled / SSR render
// looks right; the inline order set per-section below overrides.
// What needs the keeper first (owner, 2026-10-21): on an iPhone 13 Today
// started at the bottom edge of the first screen, under the score and an
// empty photo timeline.
export const DEFAULT_LAYOUT = [
  'whatsnew',
  'welcome',
  'today',
  'score',
  'controller',
  'params',
  'residents',
  'journal',
  'photos',
  'tip',
];
// The order before 2026-10-21. A layout saved before then whose sections
// still run in this order (a section hidden, missing or added since does not
// count) was never arranged by the keeper, so it takes the new default; any
// order the keeper changed stays theirs.
const PREVIOUS_DEFAULT = [
  'whatsnew',
  'score',
  'controller',
  'photos',
  'today',
  'welcome',
  'params',
  'journal',
  'residents',
  'tip',
];
// Sections merged into one. "Sensei's update" and "Today's plan" became
// Today (owner, 2026-09-27); a saved layout keeps Today where the first of
// them was.
const RENAMED_SECTIONS = { update: 'today', plan: 'today' };

// Every layout saved from 2026-10-21 on ends with this entry: from then on
// the order is the keeper's, even one that matches the old default. An
// older build drops it as an unknown key.
export const LAYOUT_MARK = '~2026-10';

function keptPreviousOrder(keys) {
  const at = keys.filter((k) => k !== 'whatsnew').map((k) => PREVIOUS_DEFAULT.indexOf(k));
  return at.every((p, i) => p >= 0 && (i === 0 || p > at[i - 1]));
}

// Drop unknown keys (renamed sections) + append missing ones (new
// sections) so a saved layout from an older build still works.
export function mergeLayout(arr) {
  const seen = new Set();
  const cleaned = [];
  for (const raw of arr || []) {
    const k = RENAMED_SECTIONS[raw] || raw;
    if (DEFAULT_LAYOUT.includes(k) && !seen.has(k)) {
      cleaned.push(k);
      seen.add(k);
    }
  }
  const marked = Array.isArray(arr) && arr.includes(LAYOUT_MARK);
  if (!marked && keptPreviousOrder(cleaned)) return [...DEFAULT_LAYOUT];
  for (const k of DEFAULT_LAYOUT) {
    if (!seen.has(k)) cleaned.push(k);
  }
  // What's New is an announcement banner, not a rearrangeable tank
  // section — always pin it to the very top, overriding any older saved
  // order that had it at the bottom. (It renders nothing when there's no
  // news, so it never leaves an empty gap.)
  const i = cleaned.indexOf('whatsnew');
  if (i > 0) {
    cleaned.splice(i, 1);
    cleaned.unshift('whatsnew');
  }
  return cleaned;
}

// A saved layout is the section order, with a hidden section written as
// "-key" (owner, 2026-09-27: remove a section, add it back later). An
// older build reads "-key" as unknown and shows the section again, which
// is the safe way round.
export function decodeLayout(raw) {
  const keys = [];
  const hidden = new Set();
  for (const entry of Array.isArray(raw) ? raw : []) {
    if (typeof entry !== 'string') continue;
    const off = entry.startsWith('-');
    const base = off ? entry.slice(1) : entry;
    const k = RENAMED_SECTIONS[base] || base;
    keys.push(k);
    if (off) hidden.add(k);
  }
  const order = mergeLayout(keys.length ? keys : DEFAULT_LAYOUT);
  for (const k of hidden) if (!order.includes(k) || k === 'whatsnew') hidden.delete(k);
  return { order, hidden };
}

export function encodeLayout(order, hidden) {
  return [...order.map((k) => (hidden.has(k) ? `-${k}` : k)), LAYOUT_MARK];
}

export function layoutKey(raw) {
  return Array.isArray(raw) ? raw.join('|') : null;
}

// The layout Home starts from. `session` is this tab's last save, with the
// server layout it was made on top of (`serverKey`). While the server copy
// is still that one, the save is newer and wins; once the server copy has
// moved on (our save came back, or another device saved), the server wins.
// Without either, the localStorage cache, then the default.
export function pickInitialLayout({ server, session, cached } = {}) {
  if (session && Array.isArray(session.layout) && session.serverKey === layoutKey(server)) {
    return session.layout;
  }
  if (Array.isArray(server)) return server;
  if (Array.isArray(cached)) return cached;
  return DEFAULT_LAYOUT.slice();
}
